import { ChevronDown, type LucideIcon, X } from 'lucide-react';
import { useState, useRef, useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import type { UseFormRegister, FieldErrors, FieldValues, Path } from 'react-hook-form';

import RequiredMark from '@/shared/components/ui/RequiredMark';

interface ISelectOption {
  value: string;
  label: string;
  disabled?: boolean;
  color?: string; // Optional color class for the option
  title?: string; // Optional title/tooltip for disabled options
}

interface IFormSelectProps<T extends FieldValues> {
  name: Path<T>;
  label?: ReactNode;
  placeholder?: string;
  options: ISelectOption[];
  register: UseFormRegister<T>;
  errors: FieldErrors<T>;
  value?: string | string[];
  onChange?: (value: string | string[]) => void;
  onRemove?: (removedValue: string) => void;
  showRequiredMark?: boolean;
  validation?: {
    required?: string | boolean;
    validate?: Record<string, (value: string | string[]) => string | boolean>;
  };
  icon?: LucideIcon;
  disabled?: boolean;
  className?: string;
  containerClassName?: string;
  optionClassName?: string;
  maxHeight?: number;
  searchable?: boolean;
  multiple?: boolean;
  openUpward?: boolean;
}

const FormSelect = <T extends FieldValues>({
  name,
  label,
  placeholder = 'Select an option',
  options,
  register,
  errors,
  value,
  onChange,
  onRemove,
  validation,
  icon: Icon,
  disabled = false,
  className = '',
  containerClassName = '',
  optionClassName = '',
  maxHeight = 200,
  searchable = false,
  multiple = false,
  openUpward = false,
  showRequiredMark = true,
}: IFormSelectProps<T>) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
  const getInitialValues = (): string[] => {
    if (multiple) {
      if (Array.isArray(value)) {
        return value;
      }
      return typeof value === 'string' ? [value] : [];
    }
    return typeof value === 'string' ? [value] : [];
  };

  const [selectedValues, setSelectedValues] = useState<string[]>(getInitialValues());
  const selectRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const hiddenSelectRef = useRef<HTMLSelectElement>(null);
  const prevValueRef = useRef<string | string[] | undefined>(value);
  const isInternalUpdateRef = useRef<boolean>(false);
  const optionRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [dropdownPosition, setDropdownPosition] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);

  const error = errors[name];
  const errorMessage = error?.message as string;

  const registerProps = register(name, validation);

  // Filter options based on search term
  const filteredOptions = searchable
    ? options.filter((option) => option.label.toLowerCase().includes(searchTerm.toLowerCase()))
    : options;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        selectRef.current &&
        !selectRef.current.contains(target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(target)
      ) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus input when dropdown opens in searchable mode
  useEffect(() => {
    if (isOpen && searchable && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [isOpen, searchable]);

  // Reset highlighted index when dropdown closes
  useEffect(() => {
    if (!isOpen) {
      setHighlightedIndex(-1);
    }
  }, [isOpen]);

  // Reset highlighted index when search term changes (for searchable mode)
  useEffect(() => {
    if (isOpen && searchable) {
      setHighlightedIndex(-1);
    }
  }, [searchTerm, isOpen, searchable]);

  // Scroll highlighted option into view
  useEffect(() => {
    if (
      highlightedIndex >= 0 &&
      highlightedIndex < filteredOptions.length &&
      optionRefs.current[highlightedIndex]
    ) {
      optionRefs.current[highlightedIndex]?.scrollIntoView({
        block: 'nearest',
        behavior: 'smooth',
      });
    }
  }, [highlightedIndex, filteredOptions.length]);

  // Calculate dropdown position when it opens
  useEffect(() => {
    if (isOpen && selectRef.current) {
      const rect = selectRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const dropdownHeight = Math.min(maxHeight, filteredOptions.length * 40 + 16); // Approximate height

      // Determine if should open upward
      let opensUpward = false;
      if (openUpward) {
        opensUpward = true;
      } else if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
        opensUpward = true;
      }

      // Calculate position for portal (fixed positioning)
      // Add 4px margin (mb-1 equivalent) when opening upward
      setDropdownPosition({
        top: opensUpward ? rect.top - dropdownHeight - 4 : rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      });
    } else {
      setDropdownPosition(null);
    }
  }, [isOpen, maxHeight, filteredOptions.length, openUpward]);

  // Update dropdown position on scroll/resize
  useEffect(() => {
    if (!isOpen || !selectRef.current) {
      return;
    }

    const updatePosition = () => {
      if (selectRef.current) {
        const rect = selectRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;
        const dropdownHeight = Math.min(maxHeight, filteredOptions.length * 40 + 16);

        // Recalculate direction
        let opensUpward = false;
        if (openUpward) {
          opensUpward = true;
        } else if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
          opensUpward = true;
        }

        setDropdownPosition({
          top: opensUpward ? rect.top - dropdownHeight - 4 : rect.bottom + 4,
          left: rect.left,
          width: rect.width,
        });
      }
    };

    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen, maxHeight, filteredOptions.length, openUpward]);

  // Update selectedValues when value prop changes (e.g. parent normalizes selection like removing "All")
  useEffect(() => {
    if (isInternalUpdateRef.current) {
      isInternalUpdateRef.current = false;
    }
    if (value !== undefined && JSON.stringify(value) !== JSON.stringify(prevValueRef.current)) {
      let newValues: string[];
      if (multiple) {
        if (Array.isArray(value)) {
          newValues = value;
        } else if (typeof value === 'string') {
          newValues = [value];
        } else {
          newValues = [];
        }
      } else {
        newValues = typeof value === 'string' ? [value] : [];
      }
      setSelectedValues(newValues);
      prevValueRef.current = value;
    }
  }, [value, multiple]);

  // Handle option selection
  const handleSelect = (option: ISelectOption) => {
    if (option.disabled) {
      return;
    }

    let newSelectedValues: string[];

    if (multiple) {
      // Toggle selection for multiselect
      if (selectedValues.includes(option.value)) {
        newSelectedValues = selectedValues.filter((v) => v !== option.value);
      } else {
        newSelectedValues = [...selectedValues, option.value];
      }
    } else {
      // Single select behavior
      newSelectedValues = [option.value];
      setIsOpen(false);
      setSearchTerm('');
    }

    setSelectedValues(newSelectedValues);

    // Mark this as an internal update to prevent the useEffect from triggering
    isInternalUpdateRef.current = true;

    // Call custom onChange if provided (parent may normalize value, e.g. remove "All" when selecting specific)
    if (onChange) {
      onChange(multiple ? newSelectedValues : newSelectedValues[0] || '');
    }
    // Sync with react-hook-form. For multiple, selected state is driven by React (selected prop on options);
    // we still notify RHF so form state and validation use the new value.
    if (hiddenSelectRef.current) {
      if (!multiple) {
        hiddenSelectRef.current.value = newSelectedValues[0] || '';
      }
      const changeEvent = new Event('change', { bubbles: true });
      hiddenSelectRef.current.dispatchEvent(changeEvent);
      registerProps.onChange({
        target: {
          name: registerProps.name,
          value: multiple ? newSelectedValues : newSelectedValues[0] || '',
        },
      });
    }
  };

  // Handle removing a selected item in multiselect mode
  const handleRemoveItem = (valueToRemove: string) => {
    const newSelectedValues = selectedValues.filter((v) => v !== valueToRemove);
    setSelectedValues(newSelectedValues);

    // Mark this as an internal update to prevent the useEffect from triggering
    isInternalUpdateRef.current = true;

    if (onChange) {
      onChange(multiple ? newSelectedValues : newSelectedValues[0] || '');
    }

    // Call onRemove callback if provided
    if (onRemove) {
      onRemove(valueToRemove);
    }

    // For multiple, selected state is driven by React (selected prop on options). Notify react-hook-form.
    if (hiddenSelectRef.current) {
      if (!multiple) {
        hiddenSelectRef.current.innerHTML = '';
        newSelectedValues.forEach((val) => {
          const option = document.createElement('option');
          option.value = val;
          option.selected = true;
          hiddenSelectRef.current?.appendChild(option);
        });
      }
      const changeEvent = new Event('change', { bubbles: true });
      hiddenSelectRef.current.dispatchEvent(changeEvent);
      registerProps.onChange({
        target: {
          name: registerProps.name,
          value: multiple ? newSelectedValues : newSelectedValues[0] || '',
        },
      });
    }
  };

  // Toggle dropdown
  const toggleDropdown = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
      if (!isOpen) {
        setSearchTerm('');
      }
    }
  };

  // Handle input change for search
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
  };

  // Handle keyboard navigation in search input
  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (filteredOptions.length > 0) {
        if (highlightedIndex < 0) {
          // Start from first enabled option
          const firstEnabledIndex = filteredOptions.findIndex((opt) => !opt.disabled);
          setHighlightedIndex(firstEnabledIndex >= 0 ? firstEnabledIndex : 0);
        } else {
          // Continue navigation from current position
          const nextIndex = findNextEnabledOption(highlightedIndex, 'down');
          if (nextIndex === highlightedIndex && highlightedIndex < filteredOptions.length - 1) {
            // If we're at the end, wrap to first enabled option
            const firstEnabledIndex = filteredOptions.findIndex((opt) => !opt.disabled);
            setHighlightedIndex(firstEnabledIndex >= 0 ? firstEnabledIndex : 0);
          } else {
            setHighlightedIndex(nextIndex);
          }
        }
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (filteredOptions.length > 0) {
        if (highlightedIndex < 0) {
          // Start from last enabled option
          for (let i = filteredOptions.length - 1; i >= 0; i--) {
            if (!filteredOptions[i].disabled) {
              setHighlightedIndex(i);
              return;
            }
          }
        } else {
          // Continue navigation from current position
          const prevIndex = findNextEnabledOption(highlightedIndex, 'up');
          if (prevIndex === highlightedIndex && highlightedIndex > 0) {
            // If we're at the start, wrap to last enabled option
            for (let i = filteredOptions.length - 1; i >= 0; i--) {
              if (!filteredOptions[i].disabled) {
                setHighlightedIndex(i);
                return;
              }
            }
          } else {
            setHighlightedIndex(prevIndex);
          }
        }
      }
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (
        highlightedIndex >= 0 &&
        highlightedIndex < filteredOptions.length &&
        filteredOptions[highlightedIndex] &&
        !filteredOptions[highlightedIndex].disabled
      ) {
        handleSelect(filteredOptions[highlightedIndex]);
      } else if (filteredOptions.length === 1 && !filteredOptions[0].disabled) {
        // If only one option after filtering, select it
        handleSelect(filteredOptions[0]);
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      setSearchTerm('');
      setHighlightedIndex(-1);
    }
  };

  // Helper function to find next enabled option
  const findNextEnabledOption = (currentIndex: number, direction: 'up' | 'down'): number => {
    const startIndex = direction === 'down' ? currentIndex + 1 : currentIndex - 1;
    const endIndex = direction === 'down' ? filteredOptions.length : -1;
    const step = direction === 'down' ? 1 : -1;

    for (let i = startIndex; i !== endIndex; i += step) {
      if (i >= 0 && i < filteredOptions.length && !filteredOptions[i].disabled) {
        return i;
      }
    }
    return currentIndex; // Return current if no enabled option found
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (isOpen && highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
        // Select the highlighted option
        handleSelect(filteredOptions[highlightedIndex]);
      } else {
        // Open dropdown (works for both searchable and non-searchable)
        toggleDropdown();
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      setSearchTerm('');
      setHighlightedIndex(-1);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!isOpen) {
        // Open dropdown and highlight first enabled option
        setIsOpen(true);
        const firstEnabledIndex = filteredOptions.findIndex((opt) => !opt.disabled);
        setHighlightedIndex(firstEnabledIndex >= 0 ? firstEnabledIndex : 0);
      } else {
        // Move to next enabled option
        const nextIndex = findNextEnabledOption(highlightedIndex, 'down');
        if (nextIndex === highlightedIndex && highlightedIndex < filteredOptions.length - 1) {
          // If we're at the end, try to find any enabled option
          const firstEnabledIndex = filteredOptions.findIndex((opt) => !opt.disabled);
          setHighlightedIndex(firstEnabledIndex >= 0 ? firstEnabledIndex : 0);
        } else {
          setHighlightedIndex(nextIndex);
        }
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (isOpen) {
        // Move to previous enabled option
        const prevIndex = findNextEnabledOption(highlightedIndex, 'up');
        if (prevIndex === highlightedIndex && highlightedIndex > 0) {
          // If we're at the start, try to find last enabled option
          for (let i = filteredOptions.length - 1; i >= 0; i--) {
            if (!filteredOptions[i].disabled) {
              setHighlightedIndex(i);
              return;
            }
          }
        } else {
          setHighlightedIndex(prevIndex);
        }
      }
    }
  };

  const selectedOptions = options.filter((opt) => selectedValues.includes(opt.value));

  let displayText: string;
  if (multiple) {
    displayText = selectedOptions.length > 0 ? `${selectedOptions.length} selected` : placeholder;
  } else {
    displayText = selectedOptions[0]?.label || placeholder;
  }

  let displayTextColorClass = 'text-gray-500';
  if (disabled) {
    displayTextColorClass = 'text-gray-600';
  } else if (selectedOptions.length > 0) {
    displayTextColorClass = selectedOptions[0]?.color || 'text-gray-900';
  }

  return (
    <div className={`relative ${containerClassName}`}>
      {label && (
        <label htmlFor={name} className='block text-base font-semibold text-gray-700 mb-2'>
          <span className='inline-flex items-center gap-1'>
            {label}
            {showRequiredMark && !!validation?.required && <RequiredMark />}
          </span>
        </label>
      )}

      <div className='relative' ref={selectRef}>
        {/* Hidden native select for form compatibility */}
        <select
          name={registerProps.name}
          onBlur={registerProps.onBlur}
          ref={(e) => {
            registerProps.ref(e);
            hiddenSelectRef.current = e;
          }}
          value={multiple ? undefined : selectedValues[0] || ''}
          onChange={() => {}} // Handled by custom dropdown
          className='absolute opacity-0 pointer-events-none'
          tabIndex={-1}
          multiple={multiple}
        >
          {!multiple && <option value=''>{placeholder}</option>}
          {options.map((option) => (
            <option
              key={option.value}
              value={option.value}
              selected={multiple ? selectedValues.includes(option.value) : undefined}
            >
              {option.label}
            </option>
          ))}
        </select>

        {/* Custom Select Button */}
        <div
          role='button'
          tabIndex={disabled ? -1 : 0}
          onClick={toggleDropdown}
          onKeyDown={handleKeyDown}
          className={`
            w-full ${Icon ? 'pl-12' : 'pl-4'} pr-10 py-2
            border-2 rounded-xl shadow-sm bg-white cursor-pointer
            focus:outline-none focus:ring-2 focus:ring-theme-500 focus:border-transparent
            transition-all duration-200 flex items-center justify-between
            ${error ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-200'}
            ${disabled ? 'bg-gray-100 border-gray-200 cursor-not-allowed text-gray-600 pointer-events-none' : 'bg-white hover:border-gray-300'}
            ${isOpen ? 'border-theme-500 ring-2 ring-theme-500 ring-opacity-20' : ''}
            ${className}
          `}
        >
          {/* Icon */}
          {Icon && (
            <div className='absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none'>
              <Icon className='h-5 w-5 text-gray-400' />
            </div>
          )}

          {/* Selected items display */}
          <div className='flex-1 flex flex-wrap gap-1 items-center min-h-[18px] overflow-hidden'>
            {multiple && selectedOptions.length > 0 ? (
              <>
                {selectedOptions.map((option) => (
                  <span
                    key={option.value}
                    className='inline-flex items-center gap-1 px-2 py-1 bg-theme-100 text-theme-700 text-sm rounded-md'
                  >
                    <span className='truncate max-w-[120px]'>{option.label}</span>
                    <button
                      type='button'
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveItem(option.value);
                      }}
                      className='hover:bg-theme-200 rounded-full p-0.5'
                    >
                      <X className='h-3 w-3' />
                    </button>
                  </span>
                ))}
              </>
            ) : (
              <>
                {/* Selected text or search input */}
                {searchable && isOpen ? (
                  <input
                    ref={inputRef}
                    type='text'
                    value={searchTerm}
                    onChange={handleInputChange}
                    onKeyDown={handleInputKeyDown}
                    placeholder={displayText}
                    className='flex-1 text-left bg-transparent outline-none text-gray-900 placeholder-gray-500 truncate'
                    onClick={(e) => e.stopPropagation()}
                  />
                ) : (
                  <span
                    className={`flex-1 text-left overflow-hidden text-ellipsis whitespace-nowrap min-w-0 max-w-full ${displayTextColorClass}`}
                    title={displayText}
                  >
                    {displayText}
                  </span>
                )}
              </>
            )}
          </div>

          {/* Dropdown arrow */}
          <div className='absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none'>
            <ChevronDown
              className={`h-5 w-5 text-gray-400 transition-transform duration-200 ${
                isOpen ? 'rotate-180' : ''
              }`}
            />
          </div>
        </div>

        {/* Custom Dropdown - Rendered via Portal to escape modal overflow */}
        {isOpen &&
          dropdownPosition &&
          typeof document !== 'undefined' &&
          createPortal(
            <div
              ref={dropdownRef}
              className={`
              fixed bg-white border-2 border-gray-200
              rounded-xl shadow-lg z-[9999] overflow-hidden
              ${error ? 'border-red-300' : 'border-gray-200'}
            `}
              style={{
                top: `${dropdownPosition.top}px`,
                left: `${dropdownPosition.left}px`,
                width: `${dropdownPosition.width}px`,
                maxHeight: `${maxHeight}px`,
              }}
            >
              {/* Options Container with Scroll */}
              <div
                className='overflow-y-auto custom-scrollbar'
                style={{ maxHeight: `${maxHeight}px` }}
              >
                {filteredOptions.length > 0 ? (
                  filteredOptions.map((option, index) => (
                    <div
                      key={option.value}
                      ref={(el) => {
                        optionRefs.current[index] = el;
                      }}
                      role='option'
                      tabIndex={0}
                      aria-selected={highlightedIndex === index}
                      onClick={() => handleSelect(option)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleSelect(option);
                        }
                      }}
                      onMouseEnter={() => setHighlightedIndex(index)}
                      title={option.disabled && option.title ? option.title : undefined}
                      className={`
                      px-4 py-2 cursor-pointer transition-colors duration-150 border-b border-gray-50 dark:border-gray-500 last:border-b-0
                      focus:outline-none focus:bg-gray-50 focus:dark:bg-gray-700
                      ${
                        option.disabled
                          ? 'text-gray-400 cursor-not-allowed bg-gray-50'
                          : 'text-gray-900 hover:bg-theme-50'
                      }
                      ${selectedValues.includes(option.value) ? 'bg-theme-50 text-theme-700 font-medium' : ''}
                      ${highlightedIndex === index && !option.disabled ? 'bg-theme-100' : ''}
                      ${optionClassName}
                    `}
                    >
                      <div className='flex items-center justify-between'>
                        <span className={`${option.color || ''} ${optionClassName}`}>
                          {option.label}
                        </span>
                        {multiple && selectedValues.includes(option.value) && (
                          <div className='w-4 h-4 bg-theme-500 rounded-sm flex items-center justify-center'>
                            <svg
                              className='w-3 h-3 text-white'
                              fill='currentColor'
                              viewBox='0 0 20 20'
                            >
                              <path
                                fillRule='evenodd'
                                d='M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z'
                                clipRule='evenodd'
                              />
                            </svg>
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className='px-4 py-6 text-center text-gray-500 text-sm'>
                    {searchable && searchTerm ? 'No options found' : 'No options available'}
                  </div>
                )}
              </div>
            </div>,
            document.body
          )}
      </div>

      {/* Error Message */}
      {error && <p className='mt-2 text-sm text-red-600 font-medium'>{errorMessage}</p>}

      {/* Custom Scrollbar Styles */}
      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
          height: 8px;
        }

        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f8fafc;
        }

        .custom-scrollbar::-webkit-scrollbar-corner {
          background: #f8fafc;
        }

        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 3px;
        }

        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }

        .dark .custom-scrollbar::-webkit-scrollbar-track {
          background: #374151;
        }

        .dark .custom-scrollbar::-webkit-scrollbar-corner {
          background: #374151;
        }

        .dark .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #4b5563;
          border-radius: 3px;
        }

        .dark .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #6b7280;
        }

        /* Firefox */
        .custom-scrollbar {
          scrollbar-width: thin;
          scrollbar-color: #cbd5e1 #f8fafc;
        }

        .dark .custom-scrollbar {
          scrollbar-color: #4b5563 #374151;
        }
      `}</style>
    </div>
  );
};

export default FormSelect;
